import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { apiErrorMessage, issueDevice } from '@/services/api'
import {
  DEVICE_NAME_PATTERN,
  DEVICE_TYPES,
  SITE_ID_PATTERN,
  type DeviceType,
  type IssueDeviceResult,
} from '@/types/device'

interface IssueDeviceDialogProps {
  open: boolean
  siteIds: string[]
  // 機材詳細から開いた場合はデバイス名を固定する
  fixedDeviceName?: string | null
  onClose: () => void
  onIssued: (result: IssueDeviceResult) => void
}

function defaultDeviceName() {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `fork_${y}${m}${d}`
}

export default function IssueDeviceDialog({
  open,
  siteIds,
  fixedDeviceName,
  onClose,
  onIssued,
}: IssueDeviceDialogProps) {
  const { t } = useTranslation()

  const [deviceName, setDeviceName] = useState('')
  const [siteId, setSiteId] = useState('')
  const [deviceType, setDeviceType] = useState<DeviceType>('forklift')
  const [enableCredentialsProvider, setEnableCredentialsProvider] =
    useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setDeviceName(fixedDeviceName ?? defaultDeviceName())
    setSiteId('')
    setDeviceType('forklift')
    setEnableCredentialsProvider(true)
    setError(null)
  }, [open, fixedDeviceName])

  const mutation = useMutation({
    mutationFn: issueDevice,
    onSuccess: (result) => {
      onIssued(result)
    },
    onError: (err) => {
      setError(apiErrorMessage(err) ?? t('issueDialog.error'))
    },
  })

  if (!open) return null

  const deviceNameValid = DEVICE_NAME_PATTERN.test(deviceName)
  const siteIdValid = SITE_ID_PATTERN.test(siteId)
  const canSubmit = deviceNameValid && siteIdValid && !mutation.isPending

  const handleDeviceTypeChange = (value: DeviceType) => {
    setDeviceType(value)
    // フォークリフトは通常 REST API アクセスが必要
    setEnableCredentialsProvider(value === 'forklift')
  }

  const handleSubmit = () => {
    if (!canSubmit) return
    setError(null)
    mutation.mutate({
      deviceName,
      siteId,
      deviceType,
      enableCredentialsProvider,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('issueDialog.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('issueDialog.deviceName')}
            </label>
            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              disabled={Boolean(fixedDeviceName)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('issueDialog.deviceNameHint')}
            </p>
            {deviceName && !deviceNameValid && (
              <p className="mt-1 text-xs text-red-600">
                {t('issueDialog.deviceNameError')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('issueDialog.siteId')}
            </label>
            <input
              type="text"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              list="site-id-options"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <datalist id="site-id-options">
              {siteIds.map((id) => (
                <option key={id} value={id} />
              ))}
            </datalist>
            <p className="mt-1 text-xs text-gray-500">
              {t('issueDialog.siteIdHint')}
            </p>
            {siteId && !siteIdValid && (
              <p className="mt-1 text-xs text-red-600">
                {t('issueDialog.siteIdError')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('issueDialog.deviceType')}
            </label>
            <select
              value={deviceType}
              onChange={(e) =>
                handleDeviceTypeChange(e.target.value as DeviceType)
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {DEVICE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`deviceType.${type}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={enableCredentialsProvider}
                onChange={(e) => setEnableCredentialsProvider(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {t('issueDialog.credentialsProvider')}
            </label>
            <p className="mt-1 ml-6 text-xs text-gray-500">
              {t('issueDialog.credentialsProviderHint')}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isPending
              ? t('issueDialog.submitting')
              : t('issueDialog.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}
